import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  OnInit,
  Renderer2,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { AuthService } from '@pinnakl/auth/providers';
import { AuthRecoveryPanelComponent } from '@pinnakl/auth/shared';
import { CurrentUserStore } from '@pinnakl/core/data-providers';
import { PortalUserStore } from '@pinnakl/investor-portal/data-access';
import { DedicatedPortalClientService, NavigationService } from '@pinnakl/shared/util-providers';
import { ToastModule } from 'primeng/toast';
import { distinctUntilChanged, filter, take, tap } from 'rxjs';

@Component({
  imports: [AuthRecoveryPanelComponent, RouterModule, ToastModule],
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'tw:bg-surface-0' },
})
export class AppComponent implements OnInit {
  private readonly title = inject(Title);
  private readonly destroyRef = inject(DestroyRef);
  private readonly navigationService = inject(NavigationService);
  private readonly renderer = inject(Renderer2);
  private readonly authService = inject(AuthService);
  private readonly dedicatedPortalClientService = inject(DedicatedPortalClientService);
  private readonly portalUserStore = inject(PortalUserStore);
  private readonly currentUserStore = inject(CurrentUserStore);
  protected readonly user = this.currentUserStore.currentUserInfo;
  protected readonly selectedClient = this.portalUserStore.selectedClient;

  constructor() {
    this.currentUserStore.removeUser();
    this.authService.init();

    effect(() => {
      const dedicatedTitle = this.dedicatedPortalClientService.config().tabTitle;
      const selectedClient = this.portalUserStore.selectedClient();
      untracked(() => {
        this.title.setTitle(dedicatedTitle ?? `${selectedClient?.clientName ?? 'Pinnakl'} Data Room`);
      });
    });

    this.extendBodyClassesWithDedicatedPortalClientConfig();
  }

  ngOnInit(): void {
    this.authService.isAccountReady$
      .pipe(
        distinctUntilChanged(),
        filter(v => v),
        take(1),
        tap(() => {
          this.navigationService.startSaveHistory().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
        }),
      )
      .subscribe();
  }

  private extendBodyClassesWithDedicatedPortalClientConfig(): void {
    const documentBodyClass = this.dedicatedPortalClientService.config().documentBodyClass;
    if (documentBodyClass) {
      this.renderer.addClass(document.body, documentBodyClass);
    }
  }
}
